import React, { useState, Suspense, lazy, useMemo } from 'react';
import {
  Facebook,
  Instagram,
  Home,
  Building,
  Zap,
  Database,
  BarChart3,
  Download,
  FileText,
  Users,
  Settings,
  RefreshCw
} from 'lucide-react';
import { cn } from '../utils/cn';
import { JsonData, UrlHistory as UrlHistoryType, ExportOptions } from '../types';

// Lazy load heavy components
const UrlInput = lazy(() => import('./UrlInput').then(module => ({ default: module.UrlInput })));
const JsonCharts = lazy(() => import('./JsonCharts').then(module => ({ default: module.JsonCharts })));
const SearchFilter = lazy(() => import('./SearchFilter').then(module => ({ default: module.SearchFilter })));
const UrlHistory = lazy(() => import('./UrlHistory').then(module => ({ default: module.UrlHistory })));
const ScrapingInterface = lazy(() => import('./ScrapingInterface').then(module => ({ default: module.ScrapingInterface })));

interface MainContentProps {
  activeSection: string;
  jsonData: JsonData | null;
  filteredData: JsonData | null;
  statistics: any;
  isLoading: boolean;
  error: string | null;
  success: boolean;
  history: UrlHistoryType[];
  onUrlSubmit: (url: string) => void;
  onFilterChange: (filtered: JsonData) => void;
  onExport: (format: ExportOptions['format']) => void;
  onLoadHistoryUrl: (url: string) => void;
  onClearHistory: () => void;
  onScrape?: (platforms: string[], query: string) => void;
  scrapingJob?: { id: string, status: string } | null;
  onSectionChange?: (section: string) => void;
  className?: string;
}

const serviceCards = [
  {
    id: 'facebook',
    title: 'Facebook Business',
    description: 'Multi-platform sync for business pages',
    icon: Facebook,
    color: 'bg-blue-600',
    status: 'Connected'
  },
  {
    id: 'instagram',
    title: 'Instagram Real Estate',
    description: 'Property listings and social media management',
    icon: Instagram,
    color: 'bg-pink-600',
    status: 'Active'
  },
  {
    id: 'zillow',
    title: 'Zillow Premier',
    description: 'Premier real estate data integration',
    icon: Home,
    color: 'bg-orange-600',
    status: 'Premium'
  },
  {
    id: 'realtor',
    title: 'Realtor.com Pro',
    description: 'Professional real estate listings',
    icon: Building,
    color: 'bg-green-600',
    status: 'Pro'
  }
];

export const MainContent = React.memo<MainContentProps>(({
  activeSection,
  jsonData,
  filteredData,
  statistics,
  isLoading,
  error,
  success,
  history,
  onUrlSubmit,
  onFilterChange,
  onExport,
  onLoadHistoryUrl,
  onClearHistory,
  onScrape,
  scrapingJob,
  onSectionChange,
  className
}) => {

  // Memoize service cards to prevent unnecessary re-renders
  const memoizedServiceCards = useMemo(() => serviceCards, []);

  // Loading component for lazy loaded components
  const LoadingFallback = () => (
    <div className="flex items-center justify-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span className="ml-2 text-gray-600">Loading...</span>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'command':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Command Center</h2>
              <p className="text-gray-600">Central control hub for all operations</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Zap className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">System Status</h3>
                    <p className="text-sm text-gray-500">All systems operational</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-green-600">99.9%</span>
                  <span className="text-sm text-gray-500">Uptime</span>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Database className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Data Processed</h3>
                    <p className="text-sm text-gray-500">Last 24 hours</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">1.2M</span>
                  <span className="text-sm text-gray-500">Records</span>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Active Users</h3>
                    <p className="text-sm text-gray-500">Current session</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-purple-600">247</span>
                  <span className="text-sm text-gray-500">Users</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Social Media</h2>
              <p className="text-gray-600">Multi-Platform Sync</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {memoizedServiceCards.slice(0, 2).map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.id} className="bg-white rounded-lg border p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={cn('p-3 rounded-lg', card.color)}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{card.title}</h3>
                        <p className="text-sm text-gray-600">{card.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        card.status === 'Connected' ? 'bg-green-100 text-green-800' :
                        card.status === 'Active' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      )}>
                        {card.status}
                      </span>
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Manage →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'scraper':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Content Scraper</h2>
              <p className="text-gray-600">Deep extraction for LINE VOOM, Shopee, Lazada & Real Estate Portals</p>
            </div>

            <Suspense fallback={<LoadingFallback />}>
              <ScrapingInterface />
            </Suspense>
          </div>
        );

      case 'json-analyzer':
        return null;

      case 'data-visualizer':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Data Visualizer</h2>
              <p className="text-gray-600">Visualize your JSON data with interactive charts</p>
            </div>

            {jsonData ? (
              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <Suspense fallback={<LoadingFallback />}>
                  <JsonCharts data={filteredData || jsonData} />
                </Suspense>
              </div>
            ) : (
              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <BarChart3 className="h-8 w-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Interactive Charts</h3>
                    <p className="text-gray-600">Create beautiful visualizations from your JSON data</p>
                  </div>
                </div>

                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h4 className="text-lg font-medium mb-2">No Data to Visualize</h4>
                  <p className="text-sm">Load JSON data first to create charts and visualizations</p>
                  <button
                    onClick={() => onSectionChange?.('json-analyzer')}
                    className="mt-4 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Go to JSON Analyzer
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'search-export':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Search & Export</h2>
              <p className="text-gray-600">Find, filter, and export your data</p>
            </div>

            {/* Search and Filter */}
            {jsonData && (
              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">JSON Tools</h3>
                <Suspense fallback={<LoadingFallback />}>
                  <SearchFilter
                    data={jsonData}
                    onFilter={onFilterChange}
                  />
                </Suspense>
              </div>
            )}

            {/* Export Options */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Download className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Export Data</h3>
                    <p className="text-sm text-gray-500">Download in multiple formats</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onExport('json')}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    JSON
                  </button>
                  <button
                    onClick={() => onExport('csv')}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => onExport('xlsx')}
                    className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                  >
                    Excel
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <RefreshCw className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Recent URLs</h3>
                    <p className="text-sm text-gray-500">Access previously analyzed endpoints</p>
                  </div>
                </div>
                <Suspense fallback={<LoadingFallback />}>
                  <UrlHistory
                    history={history}
                    onClearHistory={onClearHistory}
                    onLoadUrl={onLoadHistoryUrl}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        );

      case 'quantum-analysis':
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Quantum Analysis</h2>
              <p className="text-gray-600">Analyze scraped data using IBM Quantum Computing</p>
            </div>

            <div className="bg-white rounded-lg border p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Zap className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Quantum Data Analysis</h3>
                  <p className="text-gray-600">Leverage IBM Quantum to analyze real estate sales data</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Available Commands</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Analyze Thai Sales Data</span>
                      <button
                        onClick={() => {
                          // Run the npm script
                          window.open('http://localhost:3000', '_blank'); // Placeholder
                        }}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                      >
                        Run Analysis
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Quantum Configuration</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">API Key:</span> {process.env.REACT_APP_QUANTUM_API_KEY || 'Not configured'}
                    </div>
                    <div>
                      <span className="font-medium">Instance CRN:</span> Configured
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h2>
              <p className="text-gray-600">Welcome to your AXA-style JSON Dashboard</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memoizedServiceCards.map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.id} className="bg-white rounded-lg border p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={cn('p-3 rounded-lg', card.color)}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{card.title}</h3>
                        <p className="text-sm text-gray-600">{card.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        card.status === 'Connected' ? 'bg-green-100 text-green-800' :
                        card.status === 'Active' ? 'bg-blue-100 text-blue-800' :
                        card.status === 'Premium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      )}>
                        {card.status}
                      </span>
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Manage →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={cn('flex-1 p-8 bg-gray-50 min-h-screen', className)}>
      {renderContent()}
    </div>
  );
});

MainContent.displayName = 'MainContent';
